import {
  pgTable,
  text,
  timestamp,
  uuid,
  integer,
  numeric,
  boolean,
  jsonb,
  index,
  uniqueIndex,
  check,
} from "drizzle-orm/pg-core";
import { sql } from "drizzle-orm";
import { bookings } from "./calendar";

// ---------------------------------------------------------------------------
// Timestamp helper
// ---------------------------------------------------------------------------

const tz = { withTimezone: true } as const;

// ---------------------------------------------------------------------------
// Service Delivery Engine — Core Tables
// ---------------------------------------------------------------------------
// Universal engine for ALL service businesses. One schema, configured per niche.
// Architecture: docs/architecture/SERVICE-DELIVERY-ENGINE.md
//
// Core abstraction:
//   SERVICE (what) + RESOURCES (who/what) + TIME (when) + LOCATION (where)
//   + CUSTOMER (for whom) = BOOKING
//
// Covers: tutoring, fitness, salon, plumber, cleaning, photography, agency,
//         medical clinic, moving, consulting, and every niche in between.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Service Catalog — what the business offers
// ---------------------------------------------------------------------------

export const DELIVERY_MODES = [
  "individual",
  "group",
  "self_paced",
  "hybrid",
] as const;
export type DeliveryMode = (typeof DELIVERY_MODES)[number];

export const BOOKING_FLOWS = ["appointment", "walk_in", "both"] as const;
export type BookingFlow = (typeof BOOKING_FLOWS)[number];

export const INTAKE_REQUIREMENTS = [
  "first_booking",
  "every_booking",
  "never",
] as const;
export type IntakeRequirement = (typeof INTAKE_REQUIREMENTS)[number];

export const LOCATION_MODES = [
  "business",
  "customer",
  "virtual",
  "flexible",
] as const;
export type LocationMode = (typeof LOCATION_MODES)[number];

export interface RequiredResource {
  resourceType: string;
  role?: string;
  quantity: number;
  skillsRequired?: string[];
  tags?: string[];
}

export interface BookingRules {
  minAdvanceHours?: number;
  maxAdvanceDays?: number;
  lateCancelHours?: number;
  emergencyPassesMax?: number;
  noShowPolicy?: string;
  bufferBefore?: number;
  bufferAfter?: number;
  autoConfirm?: boolean;
  customerCanBook?: boolean;
  reminderSettings?: {
    enabled?: boolean;
    offsetsMinutes?: number[];
    channels?: Array<"email" | "sms">;
  };
}

export interface PricingTier {
  name: string;
  priceAmount: number;
  creditCost: number;
}

export interface WaitlistConfig {
  mode: "auto_add" | "first_to_claim";
  claimTimeoutMinutes?: number;
}

export const serviceCatalog = pgTable(
  "service_catalog",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    name: text("name").notNull(),
    description: text("description"),
    deliveryMode: text("delivery_mode").notNull(), // DeliveryMode
    durationMinutes: integer("duration_minutes").notNull(),
    locationMode: text("location_mode").notNull().default("business"), // LocationMode
    requiredResources: jsonb("required_resources")
      .$type<RequiredResource[]>()
      .notNull()
      .default([]),
    bookingRules: jsonb("booking_rules").$type<BookingRules>().default({}),
    bookingFlow: text("booking_flow").notNull().default("appointment"), // BookingFlow
    creditCost: numeric("credit_cost", { precision: 10, scale: 2 }),
    priceCents: integer("price_cents"),
    currency: text("currency").notNull().default("EUR"),
    pricingTiers: jsonb("pricing_tiers").$type<PricingTier[]>(),
    subscriptionTierAccess: text("subscription_tier_access").array(),
    maxParticipants: integer("max_participants"),
    intakeFormId: uuid("intake_form_id"),
    intakeRequired: text("intake_required").notNull().default("never"), // IntakeRequirement
    waitlistConfig: jsonb("waitlist_config").$type<WaitlistConfig>(),
    // S.2: Per-service engagement decay for PI.1 (salon=28d, HVAC=365d)
    engagementDecayConfig: jsonb("engagement_decay_config").$type<{
      expectedIntervalDays: number;
      coldThresholdMultiplier: number;
      decayFunction: "linear" | "exponential" | "step";
    }>(),
    // Layer 0.6: AI discovery — public-facing fields (opt-in per service)
    publicDescription: text("public_description"),
    isPubliclyListed: boolean("is_publicly_listed").notNull().default(false),
    isActive: boolean("is_active").notNull().default(true),
    sortOrder: integer("sort_order").notNull().default(0),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_svc_catalog_tenant").on(table.tenantId),
    index("idx_svc_catalog_active").on(table.tenantId, table.isActive),
    check(
      "chk_svc_delivery_mode",
      sql`${table.deliveryMode} IN ('individual', 'group', 'self_paced', 'hybrid')`,
    ),
  ],
);

// ---------------------------------------------------------------------------
// Resources — people, rooms, equipment, vehicles
// ---------------------------------------------------------------------------

export const RESOURCE_TYPES = [
  "person",
  "room",
  "equipment",
  "vehicle",
  "virtual",
] as const;
export type ResourceType = (typeof RESOURCE_TYPES)[number];

export const EMPLOYMENT_TYPES = [
  "employee",
  "subcontractor",
  "booth_renter",
] as const;
export type EmploymentType = (typeof EMPLOYMENT_TYPES)[number];

export interface CommissionConfig {
  type: "flat" | "percentage" | "tiered";
  value?: number;
  tiers?: Array<{ minRevenue: number; rate: number }>;
}

export interface SubcontractorDetails {
  companyName?: string;
  rateType?: string;
  rate?: number;
  insuranceExpiry?: string;
  licenseExpiry?: string;
}

export const resources = pgTable(
  "resources",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    name: text("name").notNull(),
    resourceType: text("resource_type").notNull(), // ResourceType
    staffMemberId: text("staff_member_id"), // Better Auth user ID if person
    skills: text("skills").array().notNull().default([]),
    capacity: integer("capacity").notNull().default(1),
    homeLocationId: uuid("home_location_id"), // default business location
    commissionConfig: jsonb("commission_config").$type<CommissionConfig>(),
    employmentType: text("employment_type").default("employee"), // EmploymentType
    subcontractorDetails:
      jsonb("subcontractor_details").$type<SubcontractorDetails>(),
    // SC-7: taxId is sensitive PII — stored as separate encrypted column, NOT in JSONB
    subcontractorTaxIdEncrypted: text("subcontractor_tax_id_encrypted"),
    subcontractorTaxIdHash: text("subcontractor_tax_id_hash"), // HMAC for lookup
    encryptionKeyId: text("encryption_key_id"), // KEK version for encrypted fields
    // Layer 0.6: AI discovery — public-facing fields (opt-in per resource)
    publicName: text("public_name"),
    publicBio: text("public_bio"),
    isPubliclyListed: boolean("is_publicly_listed").notNull().default(false),
    isActive: boolean("is_active").notNull().default(true),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_resources_tenant").on(table.tenantId),
    index("idx_resources_type").on(table.tenantId, table.resourceType),
    index("idx_resources_staff").on(table.tenantId, table.staffMemberId),
  ],
);

// ---------------------------------------------------------------------------
// Resource Schedules — weekly availability per resource
// ---------------------------------------------------------------------------

export const resourceSchedules = pgTable(
  "resource_schedules",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    resourceId: uuid("resource_id").notNull(),
    dayOfWeek: integer("day_of_week").notNull(), // 0=Sunday, 6=Saturday
    startTime: text("start_time").notNull(), // "09:00" (HH:MM)
    endTime: text("end_time").notNull(), // "17:00"
    timezone: text("timezone").notNull().default("UTC"),
    locationId: uuid("location_id"), // multi-location: which location this day
    effectiveFrom: timestamp("effective_from", tz),
    effectiveTo: timestamp("effective_to", tz),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_res_sched_tenant").on(table.tenantId),
    index("idx_res_sched_resource_day").on(
      table.tenantId,
      table.resourceId,
      table.dayOfWeek,
    ),
    check(
      "chk_res_sched_day",
      sql`${table.dayOfWeek} >= 0 AND ${table.dayOfWeek} <= 6`,
    ),
  ],
);

// ---------------------------------------------------------------------------
// Resource Blocks — time-off, maintenance, holidays
// ---------------------------------------------------------------------------

export const BLOCK_REASONS = [
  "time_off",
  "holiday",
  "maintenance",
  "training",
  "personal",
  "other",
] as const;
export type BlockReason = (typeof BLOCK_REASONS)[number];

export const resourceBlocks = pgTable(
  "resource_blocks",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    resourceId: uuid("resource_id").notNull(),
    startAt: timestamp("start_at", tz).notNull(),
    endAt: timestamp("end_at", tz).notNull(),
    reason: text("reason").notNull().default("time_off"), // BlockReason
    notes: text("notes"),
    isRecurring: boolean("is_recurring").notNull().default(false),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_res_blocks_tenant").on(table.tenantId),
    index("idx_res_blocks_resource_time").on(
      table.tenantId,
      table.resourceId,
      table.startAt,
    ),
  ],
);

// ---------------------------------------------------------------------------
// Service Bookings — the actual service delivery instance
// ---------------------------------------------------------------------------
// This is the SD engine's rich booking. The simpler calendar.bookings table
// handles calendar sync (Google/MS). calendarEventId optionally links them
// when calendar sync is active. Walk-ins and self-paced have no calendar event.

export const SERVICE_BOOKING_STATUSES = [
  "requested",
  "confirmed",
  "in_progress",
  "completed",
  "cancelled",
  "no_show",
  "late_cancelled",
  "waitlisted",
] as const;
export type ServiceBookingStatus = (typeof SERVICE_BOOKING_STATUSES)[number];

export const PAYMENT_METHODS = [
  "credit",
  "subscription",
  "direct_payment",
  "complimentary",
  "gift_card",
] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];

export interface ClientRecurrence {
  pattern: string; // "every_3_weeks", "biweekly", "monthly"
  preferredDay?: number; // 0-6
  preferredTime?: string; // "09:00"
  timezone?: string;
  autoBook?: boolean;
}

export interface PaymentBreakdown {
  items: Array<{
    method: string;
    amountCents: number;
    referenceId?: string;
  }>;
}

export const serviceBookings = pgTable(
  "service_bookings",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    serviceId: uuid("service_id").notNull(),
    personId: uuid("person_id").notNull(),
    // Optional link to calendar.bookings for Google/MS sync
    calendarEventId: uuid("calendar_event_id").references(() => bookings.id, {
      onDelete: "set null",
    }),
    jobId: uuid("job_id"), // FK to jobs if part of multi-booking project
    startAt: timestamp("start_at", tz).notNull(),
    endAt: timestamp("end_at", tz).notNull(),
    status: text("status").notNull().default("requested"), // ServiceBookingStatus
    assignedResources: jsonb("assigned_resources")
      .$type<Array<{ resourceId: string; role?: string }>>()
      .notNull()
      .default([]),
    locationMode: text("location_mode").notNull().default("business"), // LocationMode
    locationAddress: text("location_address"),
    customerPropertyId: uuid("customer_property_id"),
    paidVia: text("paid_via"), // PaymentMethod
    selectedTierId: text("selected_tier_id"),
    creditDeducted: numeric("credit_deducted", { precision: 10, scale: 2 }),
    pricePaidCents: integer("price_paid_cents"),
    commissionAmount: numeric("commission_amount", { precision: 10, scale: 2 }),
    internalCost: numeric("internal_cost", { precision: 10, scale: 2 }),
    margin: numeric("margin", { precision: 10, scale: 2 }),
    paymentBreakdown: jsonb("payment_breakdown").$type<PaymentBreakdown>(),
    tipAmount: numeric("tip_amount", { precision: 10, scale: 2 }),
    clientRecurrence: jsonb("client_recurrence").$type<ClientRecurrence>(),
    capacityOverride: integer("capacity_override"),
    estimatedWaitMinutes: integer("estimated_wait_minutes"), // walk-in queue
    checkedInAt: timestamp("checked_in_at", tz), // kiosk self-service or staff check-in
    customerNotes: text("customer_notes"),
    internalNotes: text("internal_notes"),
    cancelledAt: timestamp("cancelled_at", tz),
    cancelledBy: text("cancelled_by"),
    cancellationReason: text("cancellation_reason"),
    source: text("source").notNull().default("manual"), // manual, online, walk_in, workflow, api
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_svc_bookings_tenant").on(table.tenantId),
    index("idx_svc_bookings_person").on(table.tenantId, table.personId),
    index("idx_svc_bookings_service_time").on(
      table.tenantId,
      table.serviceId,
      table.startAt,
    ),
    index("idx_svc_bookings_status").on(table.tenantId, table.status),
    index("idx_svc_bookings_job").on(table.tenantId, table.jobId),
    index("idx_svc_bookings_calendar").on(table.calendarEventId),
    check(
      "chk_svc_bookings_capacity_override",
      sql`${table.capacityOverride} IS NULL OR ${table.capacityOverride} > 0`,
    ),
  ],
);

export interface BookingIntakeUploadedFile {
  fieldId: string;
  mediaUploadId: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
  url: string | null;
}

export const bookingIntakeSubmissions = pgTable(
  "booking_intake_submissions",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    bookingId: uuid("booking_id").notNull(),
    serviceId: uuid("service_id").notNull(),
    formId: uuid("form_id").notNull(),
    personId: uuid("person_id"),
    answers: jsonb("answers")
      .$type<Record<string, unknown>>()
      .notNull()
      .default({}),
    uploadedFiles: jsonb("uploaded_files")
      .$type<BookingIntakeUploadedFile[]>()
      .notNull()
      .default([]),
    source: text("source").notNull().default("public_booking"),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_booking_intake_tenant_service").on(
      table.tenantId,
      table.serviceId,
      table.createdAt,
    ),
    index("idx_booking_intake_tenant_form").on(
      table.tenantId,
      table.formId,
      table.createdAt,
    ),
    uniqueIndex("uq_booking_intake_booking").on(table.tenantId, table.bookingId),
  ],
);

// ---------------------------------------------------------------------------
// Booking Rules Overrides — per-contact exceptions
// ---------------------------------------------------------------------------

export const bookingRulesOverrides = pgTable(
  "booking_rules_overrides",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    personId: uuid("person_id").notNull(),
    serviceId: uuid("service_id"), // nullable = applies to all services
    emergencyPassesRemaining: integer("emergency_passes_remaining"),
    customLateCancelHours: integer("custom_late_cancel_hours"),
    customAdvanceHours: integer("custom_advance_hours"),
    customMaxAdvanceDays: integer("custom_max_advance_days"),
    isVip: boolean("is_vip").notNull().default(false),
    notes: text("notes"),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_booking_overrides_tenant_person").on(
      table.tenantId,
      table.personId,
    ),
  ],
);

// ---------------------------------------------------------------------------
// Booking Line Items — parts, labor, materials, add-ons
// ---------------------------------------------------------------------------

export const LINE_ITEM_TYPES = [
  "labor",
  "parts",
  "materials",
  "product",
  "add_on",
  "travel",
  "custom",
] as const;
export type LineItemType = (typeof LINE_ITEM_TYPES)[number];

export const bookingLineItems = pgTable(
  "booking_line_items",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    bookingId: uuid("booking_id"), // FK to service_bookings
    jobId: uuid("job_id"), // FK to jobs (for project-level line items)
    itemType: text("item_type").notNull(), // LineItemType
    description: text("description").notNull(),
    quantity: numeric("quantity", { precision: 10, scale: 2 })
      .notNull()
      .default("1"),
    unitPriceCents: integer("unit_price_cents").notNull(),
    costToProviderCents: integer("cost_to_provider_cents"),
    taxRate: numeric("tax_rate", { precision: 5, scale: 4 }),
    sku: text("sku"), // optional inventory link
    // S.1: Revenue classification for PI.3 revenue intelligence
    revenueType: text("revenue_type"),
    sortOrder: integer("sort_order").notNull().default(0),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_line_items_booking").on(table.tenantId, table.bookingId),
    index("idx_line_items_job").on(table.tenantId, table.jobId),
  ],
);

// ---------------------------------------------------------------------------
// Jobs — multi-booking projects (renovation = 5 visits, moving = pickup + delivery)
// ---------------------------------------------------------------------------

export const JOB_STATUSES = [
  "quoted",
  "confirmed",
  "in_progress",
  "completed",
  "invoiced",
  "cancelled",
] as const;
export type JobStatus = (typeof JOB_STATUSES)[number];

export const jobs = pgTable(
  "jobs",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    personId: uuid("person_id").notNull(),
    title: text("title").notNull(),
    description: text("description"),
    status: text("status").notNull().default("quoted"), // JobStatus
    customerPropertyId: uuid("customer_property_id"),
    quotedAmountCents: integer("quoted_amount_cents"),
    actualAmountCents: integer("actual_amount_cents"),
    depositAmountCents: integer("deposit_amount_cents"),
    depositPaidAt: timestamp("deposit_paid_at", tz),
    currency: text("currency").notNull().default("EUR"),
    teamIds: text("team_ids").array().notNull().default([]),
    estimatedSessions: integer("estimated_sessions"),
    completedSessions: integer("completed_sessions").notNull().default(0),
    startedAt: timestamp("started_at", tz),
    completedAt: timestamp("completed_at", tz),
    invoicedAt: timestamp("invoiced_at", tz),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_jobs_tenant").on(table.tenantId),
    index("idx_jobs_person").on(table.tenantId, table.personId),
    index("idx_jobs_status").on(table.tenantId, table.status),
    index("idx_jobs_property").on(table.tenantId, table.customerPropertyId),
  ],
);

// ---------------------------------------------------------------------------
// Session Participants — for group sessions (fitness class, workshop, etc.)
// ---------------------------------------------------------------------------

export const PARTICIPANT_STATUSES = [
  "enrolled",
  "checked_in",
  "attended",
  "no_show",
  "cancelled",
  "waitlisted",
] as const;
export type ParticipantStatus = (typeof PARTICIPANT_STATUSES)[number];

export const sessionParticipants = pgTable(
  "session_participants",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    bookingId: uuid("booking_id").notNull(), // FK to service_bookings (group session)
    personId: uuid("person_id").notNull(),
    status: text("status").notNull().default("enrolled"), // ParticipantStatus
    creditDeducted: numeric("credit_deducted", { precision: 10, scale: 2 }),
    creditTransactionId: uuid("credit_transaction_id"),
    checkedInAt: timestamp("checked_in_at", tz),
    feedbackRating: integer("feedback_rating"), // 1-5
    feedbackComment: text("feedback_comment"),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_session_part_booking").on(table.tenantId, table.bookingId),
    index("idx_session_part_person").on(table.tenantId, table.personId),
    uniqueIndex("uq_session_part_booking_person").on(
      table.bookingId,
      table.personId,
    ),
  ],
);

// ---------------------------------------------------------------------------
// Session Notes — delivery notes, photos, checklists during session
// ---------------------------------------------------------------------------

export const NOTE_TYPES = [
  "internal",
  "customer_visible",
  "checklist",
] as const;
export type NoteType = (typeof NOTE_TYPES)[number];

export const sessionNotes = pgTable(
  "session_notes",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    bookingId: uuid("booking_id").notNull(),
    authorId: text("author_id").notNull(),
    noteType: text("note_type").notNull().default("internal"), // NoteType
    content: text("content"),
    r2Key: text("r2_key"), // photo/document attachment
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_session_notes_booking").on(table.tenantId, table.bookingId),
  ],
);

// ---------------------------------------------------------------------------
// Service Enrollments — self-paced course/program tracking
// ---------------------------------------------------------------------------

export const SERVICE_ENROLLMENT_STATUSES = [
  "active",
  "completed",
  "paused",
  "abandoned",
] as const;
export type ServiceEnrollmentStatus = (typeof SERVICE_ENROLLMENT_STATUSES)[number];

export const serviceEnrollments = pgTable(
  "service_enrollments",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    serviceId: uuid("service_id").notNull(),
    personId: uuid("person_id").notNull(),
    status: text("status").notNull().default("active"), // ServiceEnrollmentStatus
    modulesCompleted: integer("modules_completed").notNull().default(0),
    totalModules: integer("total_modules").notNull().default(0),
    timeSpentMinutes: integer("time_spent_minutes").notNull().default(0),
    lastAccessedAt: timestamp("last_accessed_at", tz),
    certificateIssuedAt: timestamp("certificate_issued_at", tz),
    startedAt: timestamp("started_at", tz).notNull().defaultNow(),
    completedAt: timestamp("completed_at", tz),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_svc_enroll_tenant").on(table.tenantId),
    index("idx_svc_enroll_person").on(table.tenantId, table.personId),
    uniqueIndex("uq_svc_enroll_service_person").on(
      table.tenantId,
      table.serviceId,
      table.personId,
    ),
  ],
);

// ---------------------------------------------------------------------------
// Walk-In Queue Entries — live queue for walk-in businesses
// ---------------------------------------------------------------------------

export const WALK_IN_STATUSES = [
  "waiting",
  "called",
  "serving",
  "completed",
  "no_show",
] as const;
export type WalkInStatus = (typeof WALK_IN_STATUSES)[number];

export const walkInQueueEntries = pgTable(
  "walk_in_queue_entries",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    ticketNumber: integer("ticket_number").notNull(),
    customerName: text("customer_name"),
    serviceId: uuid("service_id"),
    status: text("status").notNull().default("waiting"),
    estimatedWaitMinutes: integer("estimated_wait_minutes").notNull().default(0),
    position: integer("position").notNull(),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    calledAt: timestamp("called_at", tz),
    servedAt: timestamp("served_at", tz),
    completedAt: timestamp("completed_at", tz),
  },
  (table) => [
    index("idx_walkin_queue_tenant").on(table.tenantId),
    index("idx_walkin_queue_status").on(table.tenantId, table.status),
    index("idx_walkin_queue_ticket").on(table.tenantId, table.ticketNumber),
    check(
      "chk_walkin_status",
      sql`${table.status} IN ('waiting', 'called', 'serving', 'completed', 'no_show')`,
    ),
  ],
);
